import {
  BubbleBadge,
  BubbleButton,
  BubbleCheckbox,
  BubbleInput,
  BubbleOverlay,
  BubbleTable,
  BubbleDrawer,
  BubbleAccordion,
  BubbleFormGroup,
  BubbleTypeahead,
  BubbleSelect,
} from '@go-client/components/BubbleUI';

import { MenuBar, TemplateDetails } from '@go-client/components';
import {
  BubbleTableFieldBehavior,
  BubbleTableFields,
  BubbleTableTemplate,
} from '@go-client/components/BubbleUI/BubbleTable';
import { BrandLogo } from '@go-client/components/UIkit/Brand/BrandLogo/BrandLogo';
import { PropertyBanner } from '@go-client/components/UIkit/Property/PropertyBanner';
import { updateTags } from '@go-client/lib/api/property';
import { bare as bareProperty } from '@go-shared/lib/property';
import {
  getSimpleDayTemplates,
  populateDayTemplate,
  updateDayTemplate,
  deleteTemplate,
  cloneSailingTemplate,
  populateSimpleDayTemplate,
  getDayTemplate,
} from '@go-client/lib/api/template';
import { hashStringToColor, hasOwnProperty } from '@go-shared/lib/utils';
import { PropertyTag } from '@go-shared/types/property';
import { Template, SimpleTemplate } from '@go-shared/types/template';
import { Popup } from '@spark-labs/avocado-ui';
import Fuse from 'fuse.js';
import React, { useEffect, useState, useMemo } from 'react';
import { RouteComponentProps } from 'react-router';

import Page from './Page';
import PageWrapper from './PageWrapper';
import {
  _dangerouslyParseUnknownStringToDate,
  getLocaleDateTimeStringUTC,
} from '@go-shared/lib/time';
import TemplateForm, { TemplateFormDataBase } from '@go-client/components/TemplateForm';
import qslib from '@go-shared/lib/querystring';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { intersection, notUndefined } from '@go-shared/lib/functional';
import { BubbleTypeaheadValue } from '@go-client/components/BubbleUI/BubbleTypeahead';
import { DEFAULT_FUSE_SETTINGS } from '@go-client/lib/fuse';
import ItineraryNavigation from '@go-client/components/Navigations/ItineraryNavigation';
import {
  resolvePlannerPath,
  resolveTemplateFeedPath,
  resolveTemplatePlannerPath,
} from '@go-client/routes';
import { camelToSentenceCase } from '@go-shared/lib/string';
import { getPropertyConfiguration } from '@go-shared/lib/propertyConfig';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import TemplateTransferForm from '@go-client/components/TemplateTransferForm/TemplateTransferForm';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { DayMarkersButton } from '@go-client/components/DayMarkersButton';
import { canLinkTemplates } from '@go-shared/lib/permissions';

interface TemplateQueryStrings {
  template?: string;
}

function getQueryStrings(): TemplateQueryStrings {
  const qs = qslib.readQs();

  const template =
    hasOwnProperty(qs, 'template') && typeof qs.template === 'string' ? qs.template : undefined;

  return { template };
}

interface TemplatesDefaultMode {
  type: 'default';
}

interface TemplatesEditMode {
  type: 'edit';
  template: Template;
}

interface TemplateTransferMode {
  type: 'transfer';
  template: Template;
}

type TemplatesMode = TemplatesDefaultMode | TemplatesEditMode | TemplateTransferMode;

interface TemplatesTableItem {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  type: string;
  eventsCount: number;
  venueDataCount: number;
  contentCount: number;
  tags: PropertyTag[];
  _source: SimpleTemplate;
}

interface TemplatesRouteParams {
  property: string;
}

interface TemplateSelectPageFilters {
  tag: Array<BubbleTypeaheadValue<PropertyTag>>;
  type: string | undefined;
  visibleFields: { [key: string]: boolean };
}

type TemplatesTableFields = BubbleTableFields<TemplatesTableItem> & {
  _visible?: boolean;
  _toggleable?: boolean;
};

const Templates: React.FC<RouteComponentProps<TemplatesRouteParams>> = props => {
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [tableItems, setTableItems] = useState<TemplatesTableItem[]>([]);
  const [templates, setTemplates] = useState<SimpleTemplate[]>([]);
  const [search, setSearch] = useState('');
  const [mode, setMode] = useState<TemplatesMode>({ type: 'default' });
  const [copyTemplateForm, setCopyTemplateForm] = useState<
    (TemplateFormDataBase & { templateId: string }) | undefined
  >(undefined);
  const [filters, setFilters] = useState<TemplateSelectPageFilters>({
    tag: [],
    type: undefined,
    visibleFields: {
      name: true,
      createdAt: false,
      updatedAt: true,
      type: false,
      tags: true,
      metadata: true,
      markers: true,
    },
  });
  const { instanceRole } = useServerInfo();
  const { activeProperty, refreshContext: refreshActiveProperty } = useActiveProperty();
  const { user } = useUserInfo();
  const [showDelete, setShowDelete] = useState<{ [key: string]: boolean }>({});
  const showType = getPropertyConfiguration(activeProperty.type, 'showType');

  const fuse = useMemo(() => {
    return new Fuse(tableItems, {
      ...DEFAULT_FUSE_SETTINGS,
      keys: ['name'],
    });
  }, [tableItems]);

  const fuseSearchResults = useMemo(() => {
    if (!search) return undefined;
    return fuse.search(search).map(s => s.item);
  }, [fuse, search]);

  let tableItemsFiltered = fuseSearchResults || tableItems;

  if (filters.tag.length > 0) {
    tableItemsFiltered = tableItemsFiltered.filter(
      ti =>
        intersection(
          ti.tags.map(t => t._id),
          filters.tag.map(t => t.item._id)
        ).length > 0
    );
  }

  if (filters.type) {
    tableItemsFiltered = tableItemsFiltered.filter(ti => ti.type === filters.type);
  }

  useEffect(() => {
    fetchTemplates().catch(console.error);
  }, [activeProperty]);

  useEffect(() => {
    setTableItems(
      templates.map(t => {
        const createdAtDate = _dangerouslyParseUnknownStringToDate(t.createdAt);
        const updatedAtDate = _dangerouslyParseUnknownStringToDate(t.updatedAt);

        const createdAt = createdAtDate ? getLocaleDateTimeStringUTC(createdAtDate) : t.createdAt;
        const updatedAt = updatedAtDate ? getLocaleDateTimeStringUTC(updatedAtDate) : t.updatedAt;

        return {
          id: t._id,
          name: t.name,
          createdAt,
          updatedAt,
          type: t.dayType,
          tags: t.tags,
          eventsCount: t.eventsCount,
          venueDataCount: t.venueDataCount,
          contentCount: t.contentCount,
          _source: t,
        };
      })
    );
    refreshSelectedTemplate().catch(console.error);
  }, [templates]);

  async function fetchTemplates() {
    setLoadingTemplates(true);

    try {
      const tmpls = (await getSimpleDayTemplates(activeProperty._id)).map(t =>
        populateSimpleDayTemplate(t, activeProperty)
      );

      setTemplates(tmpls);
    } finally {
      setLoadingTemplates(false);
    }
  }

  async function refreshSelectedTemplate() {
    const parsedQs = getQueryStrings();

    const toSelect = parsedQs.template
      ? templates.find(t => t._id === parsedQs.template)
      : mode.type === 'edit'
        ? templates.find(t => t._id === mode.template._id)
        : undefined;

    if (!toSelect) return;

    const bareTemplate = await getDayTemplate(toSelect._id);
    const template = populateDayTemplate(bareTemplate, activeProperty);

    setMode({ type: 'edit', template });
  }

  async function handleOnUpdatePropertyTags(tags: PropertyTag[]) {
    await updateTags(activeProperty._id, tags);
    await refreshActiveProperty();
  }

  async function handleOnUpdateTemplate(template: Template) {
    await updateDayTemplate(template._id, template);
    // TODO: possible optimization: instead of reloading all templates,
    // only reload the template that changed
    await fetchTemplates();
  }

  function handleOnCloseTemplateDetails() {
    setMode({ type: 'default' });
    qslib.writeQs({});
  }

  async function handleOnCopyTemplate(templateData: TemplateFormDataBase) {
    if (!copyTemplateForm) return;

    await cloneSailingTemplate(activeProperty._id, copyTemplateForm.templateId, {
      name: templateData.name,
      tags: templateData.tags.map(t => t._id),
      ...(templateData.dayTheme && { dayTheme: templateData.dayTheme }),
    });

    await fetchTemplates();

    setCopyTemplateForm(undefined);
    setSearch('');
  }

  async function handleOnEditTemplateClick(simpleTemplate: SimpleTemplate) {
    const bareTemplate = await getDayTemplate(simpleTemplate._id);
    const template = populateDayTemplate(bareTemplate, activeProperty);

    setMode({ type: 'edit', template });
    qslib.writeQs({ template: template._id });
  }

  async function handleOnTransferTemplateClick(simpleTemplate: SimpleTemplate) {
    const bareTemplate = await getDayTemplate(simpleTemplate._id);
    const template = populateDayTemplate(bareTemplate, activeProperty);

    setMode({ type: 'transfer', template });
  }

  async function handleDeleteTemplate(id: string) {
    await deleteTemplate(activeProperty._id, id);
    await fetchTemplates();
  }

  const tableFields: Array<TemplatesTableFields> = [
    {
      key: 'name',
      _toggleable: true,
    },
    {
      key: 'createdAt',
      label: 'Created',
      behavior: BubbleTableFieldBehavior.NoWrap,
      _toggleable: true,
    },
    {
      key: 'updatedAt',
      label: 'Updated',
      behavior: BubbleTableFieldBehavior.NoWrap,
      _toggleable: true,
    },
    showType
      ? {
          key: 'type',
          behavior: BubbleTableFieldBehavior.NoWrap,
          _toggleable: true,
        }
      : undefined,
    {
      key: 'tags',
      behavior: BubbleTableFieldBehavior.Condense,
      _toggleable: true,
    },
    {
      key: 'metadata',
      label: 'Stats',
      behavior: BubbleTableFieldBehavior.Condense,
      _toggleable: true,
    },
    {
      key: 'markers',
      behavior: BubbleTableFieldBehavior.Condense,
      _toggleable: true,
    },
    {
      key: 'actions',
      label: '',
      behavior: BubbleTableFieldBehavior.Condense,
      _visible: true,
    },
  ].filter(notUndefined);

  const tableTemplates: Array<BubbleTableTemplate<TemplatesTableItem>> = [
    {
      key: 'createdAt',
      render: item => <span>{item.createdAt}</span>,
    },
    {
      key: 'updatedAt',
      render: item => (
        <>
          {item.updatedAt.split(', ').map(e => (
            <div key={e} className="whitespace-nowrap">
              {e}
            </div>
          ))}
        </>
      ),
    },
    {
      key: 'type',
      render: item => <span>{camelToSentenceCase(item.type)}</span>,
    },
    {
      key: 'tags',
      render: item =>
        item.tags.length > 0 && (
          <>
            {item.tags.map(t => (
              <BubbleBadge key={t._id} className="mt-1 mr-1" color={hashStringToColor(t.name)}>
                {t.name}
              </BubbleBadge>
            ))}
          </>
        ),
    },
    {
      key: 'metadata',
      render: ({ eventsCount, venueDataCount, contentCount }) => (
        <>
          <BubbleBadge variant={eventsCount > 0 ? 'lime' : 'smoke'} className="mt-1 mr-1">
            E: {eventsCount}
          </BubbleBadge>
          <BubbleBadge variant={venueDataCount > 0 ? 'banana' : 'smoke'} className="mt-1 mr-1">
            VD: {venueDataCount}
          </BubbleBadge>
          <BubbleBadge variant={contentCount > 0 ? 'ocean' : 'smoke'} className="mt-1 mr-1">
            C: {contentCount}
          </BubbleBadge>
        </>
      ),
    },
    {
      key: 'markers',
      render: item => {
        return <DayMarkersButton template={item._source} />;
      },
    },
    {
      key: 'actions',
      render: item => (
        <>
          <BubbleButton
            className="p-2-i"
            variant="navy"
            inverse
            linkProps={{
              to: resolveTemplatePlannerPath(item._source.property._id, item._source._id),
            }}
            icon={['fass', 'chart-tree-map']}
          />
          <BubbleButton
            className="p-2-i mr-2"
            variant="navy"
            inverse
            linkProps={{ to: resolveTemplateFeedPath(item._source.property._id, item._source._id) }}
            icon={['fass', 'newspaper']}
          />
          <BubbleButton
            className="p-2-i"
            variant="slate"
            inverse
            onClick={() => handleOnEditTemplateClick(item._source)}
            icon="edit"
          />
          {canLinkTemplates(user) && (
            <>
              <BubbleButton
                className="p-2-i"
                variant="slate"
                onClick={() => {
                  setCopyTemplateForm({
                    name: '',
                    placeholder: `Copy of ${item.name}`,
                    tags: item.tags,
                    templateId: item._source._id,
                  });
                }}
                inverse
                icon="copy"
              />
              {instanceRole === 'brand' && (
                <BubbleButton
                  className="p-2-i"
                  variant="slate"
                  inverse
                  onClick={() => handleOnTransferTemplateClick(item._source)}
                  icon="arrows-turn-to-dots"
                />
              )}
              <>
                {!showDelete[item.id] && (
                  <BubbleButton
                    className="p-2-i"
                    variant="crimson"
                    inverse
                    onClick={() => {
                      setShowDelete({ ...showDelete, [item.id]: true });
                    }}
                    icon="trash-alt"
                  />
                )}
                {showDelete[item.id] && (
                  <>
                    <BubbleButton
                      className="p-2-i"
                      variant="crimson"
                      inverse
                      onClick={() => handleDeleteTemplate(item._source._id)}
                      icon="trash-check"
                    />
                    <BubbleButton
                      className="p-2-i"
                      variant="slate"
                      inverse
                      onClick={() => {
                        setShowDelete({ ...showDelete, [item.id]: false });
                      }}
                      icon="trash-xmark"
                    />
                  </>
                )}
              </>
            </>
          )}
        </>
      ),
    },
  ];

  const tagOpts = activeProperty.tags.map(t => ({
    key: t._id,
    item: t,
  }));

  const templateTypeOpts = ['sea', 'port'];

  const visibleTableFields = tableFields.filter(tf =>
    tf._visible !== undefined ? tf._visible : filters.visibleFields[tf.key]
  );
  const toggleableTableFields = tableFields.filter(tf => tf._toggleable);

  return (
    <PageWrapper {...props}>
      <Page className="Templates absolute flex items-stretch">
        <MenuBar {...props} property={activeProperty} />
        <div className="SideMenu bg-velvet-darkest flex flex-col">
          <div className="bg-white flex flex-col items-center">
            <BrandLogo logo={activeProperty.brand.logo} />
            <PropertyBanner {...props} property={activeProperty} swappable />
          </div>
          <BubbleAccordion
            variant="velvet"
            className="flex-grow flex flex-col border-t border-forest-darkest"
            labels={['Filters']}
            open={[0]}
            locked={[0]}
          >
            <div className="grid gap-4 p-4">
              <BubbleFormGroup label="Tags" labelClassName="text-white-i">
                <BubbleTypeahead
                  block
                  variant="velvet"
                  placeholder="Filter tags shown..."
                  menuClassName="max-h-80 overflow-y-auto"
                  labelExtractor={opt => opt.name}
                  value={filters.tag}
                  options={tagOpts}
                  onAdd={t => setFilters({ ...filters, tag: [...filters.tag, t] })}
                  onRemove={t =>
                    setFilters({ ...filters, tag: filters.tag.filter(t2 => t.key !== t2.key) })
                  }
                  onClearAll={() => setFilters({ ...filters, tag: [] })}
                />
              </BubbleFormGroup>
              {showType && (
                <BubbleFormGroup label="Departing" labelClassName="text-white-i">
                  <BubbleSelect
                    allowUndefined
                    undefinedLabel="Any"
                    variant="velvet"
                    block
                    options={templateTypeOpts}
                    value={filters.type}
                    labelExtractor={opt => opt.toUpperCase()}
                    onValue={opt => {
                      setFilters({ ...filters, type: opt });
                    }}
                  />
                </BubbleFormGroup>
              )}
              <BubbleFormGroup label="Fields" labelClassName="text-white-i">
                <div className="grid grid-cols-1 gap-1">
                  {toggleableTableFields.map(tf => (
                    <BubbleCheckbox
                      key={tf.key}
                      variant="velvet"
                      className="text-white"
                      checked={filters.visibleFields[tf.key]}
                      onValue={chk =>
                        setFilters({
                          ...filters,
                          visibleFields: { ...filters.visibleFields, [tf.key]: chk },
                        })
                      }
                      label={tf.label || camelToSentenceCase(tf.key)}
                    />
                  ))}
                </div>
              </BubbleFormGroup>
            </div>
          </BubbleAccordion>
        </div>
        <div className="TemplatesContent bg-blue-lightest overflow-y-auto flex-grow">
          <div
            className="TemplatesHeader align-with-logo flex items-end px-5 sticky top-0
                left-0 z-10 bg-blue-lightest"
          >
            <div className="mb-3 pl-3 flex-grow flex items-end">
              <ItineraryNavigation active="templates" />
            </div>
          </div>
          <div className="TemplatesBody mx-5 content-minus-logo min-w-900">
            <BubbleOverlay loading={loadingTemplates}>
              <BubbleTable
                responsive
                striped
                headVariant="velvet"
                fields={visibleTableFields}
                items={tableItemsFiltered}
                onRowClick={item => handleOnEditTemplateClick(item._source)}
                keyExtractor={item => item.id}
                stickyHeader={130}
                templates={tableTemplates}
                showPretitle
                pretitleClassName="p-0-i"
                pretitleRender={() => (
                  <div className="px-4 pt-4 pb-1">
                    <BubbleInput
                      type="text"
                      variant="velvet"
                      block
                      value={search}
                      inputClassName="outline-none-i"
                      onValue={setSearch}
                      placeholder="Search..."
                    />
                  </div>
                )}
              />
            </BubbleOverlay>
            <div className="mt-3">&nbsp;</div>
          </div>
        </div>
        {mode.type === 'edit' && (
          <BubbleDrawer onClose={handleOnCloseTemplateDetails} containerClassName="bg-eggshell">
            <TemplateDetails
              property={bareProperty(activeProperty)}
              user={user}
              template={mode.template}
              onUpdatePropertyTags={handleOnUpdatePropertyTags}
              onUpdateTemplate={handleOnUpdateTemplate}
              onEditCanvas={() => {
                props.history.push(
                  resolveTemplatePlannerPath(activeProperty._id, mode.template._id)
                );
              }}
              onEditFeed={() => {
                props.history.push(resolveTemplateFeedPath(activeProperty._id, mode.template._id));
              }}
              onClose={handleOnCloseTemplateDetails}
              onSailingDayNameClick={(sailing, sailingDay) =>
                props.history.push(
                  resolvePlannerPath(sailing.property, sailing._id, sailingDay._id)
                )
              }
            />
          </BubbleDrawer>
        )}
        {mode.type === 'transfer' && (
          <BubbleDrawer onClose={handleOnCloseTemplateDetails} containerClassName="bg-eggshell">
            <TemplateTransferForm
              user={user}
              property={activeProperty}
              template={mode.template}
              onClose={handleOnCloseTemplateDetails}
            />
          </BubbleDrawer>
        )}
        {copyTemplateForm && (
          <Popup title="Copy Template" onClose={() => setCopyTemplateForm(undefined)}>
            <hr />
            <div className="p-4" style={{ width: '500px' }}>
              <TemplateForm
                initialForm={copyTemplateForm}
                propertyTags={activeProperty.tags}
                onSubmit={handleOnCopyTemplate}
                onUpdatePropertyTags={handleOnUpdatePropertyTags}
              />
            </div>
          </Popup>
        )}
      </Page>
    </PageWrapper>
  );
};

export default Templates;
